import { CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppComponent } from './app.component';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { FormsModule } from '@angular/forms';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { AppRoutingModule } from './app-routing.module';
import { AppMaterialImporterModule } from './app-material-importer.module';
import { SharedUtilitiesModule } from '../../src/shared/shared.module';
import { appStateReducer } from './state/reducers';
import { LoadStudyDialogComponent } from '../shared/components/load-study-dialog/load-study-dialog.component';
import { SaveStudyDialogComponent } from '../shared/components/save-dialog/save-study-dialog.component';
import { CommitStudyDialogComponent } from '../shared/components/commit-dialog/commit-dialog.component';
import { PrintViewComponent } from '../shared/components/print-view/print-view.component';
import { AboutComponent } from './about/about.component';
import { AuditTrailComponent } from './about/audit-trail/audit-trail.component';
import { UsersComponent } from './about/users/users.component';
import { AddUserDialogComponent } from './about/users/add-user-dialog/add-user-dialog.component';
import { EditUserDialogComponent } from './about/users/edit-user-dialog/edit-user-dialog.component';
import { AppEffects } from './state/effects';
import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import { MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent } from '@azure/msal-angular';
import { environment } from '../environments/environment';
import { AuthTokenInterceptor } from './core/interceptors/auth-token.interceptor';
import { AuthExpiryInterceptor } from './core/interceptors/auth-expiry.interceptor';
import { ServerErrorInterceptor } from './core/interceptors/server-error.interceptor';
import { ApiLoggingInterceptor } from './core/interceptors/api-logging.interceptor';
import { SampleErrorsComponent } from './sample-errors-module/sample-errors/sample-errors.component';
import { RunStatusComponent } from './sample-errors-module/run-status/run-status.component';
import { InstrumentErrorsComponent } from './sample-errors-module/instrument-errors/instrument-errors.component';
import { RerunSamplesComponent } from './sample-errors-module/rerun-samples/rerun-samples.component';
import { authBypassProviders } from '../e2e/auth-bypass.providers';

/**
 * Callback for MSAL logging.
 * @param logLevel The log level.
 * @param message The log message to be output.
 */
export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}
/**
 * Factory function to create an MSAL public client application instance.
 * Used for handling Microsoft identity authentication.
 * @returns {IPublicClientApplication} A configured MSAL client.
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: '/',
      postLogoutRedirectUri: '/'
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}
/**
 * Factory function for configuring the MSAL HTTP interceptor.
 * Maps protected resources to scopes for token acquisition.
 * @returns {MsalInterceptorConfiguration} Interceptor configuration object.
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.apiConfig.uri, environment.apiConfig.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}
/**
 * Factory function for configuring the MSAL route guard.
 * Defines the authentication interaction type and scopes.
 * @returns {MsalGuardConfiguration} Guard configuration object.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes]
    },
    loginFailedRoute: '/login'
  };
}
/**
 * Root Angular module of the application.
 * Includes shared components, state management (NgRx), authentication (MSAL),
 * and sets up routing, animations, forms, and HTTP support.
 */
@NgModule({
  /** Components that belong to this module. */
  declarations: [
    AppComponent,
    AboutComponent,
    AuditTrailComponent,
    UsersComponent,
    AddUserDialogComponent,
    EditUserDialogComponent,
    SampleErrorsComponent,
    RunStatusComponent,
    InstrumentErrorsComponent,
    RerunSamplesComponent,
    CommitStudyDialogComponent,
    LoadStudyDialogComponent,
    SaveStudyDialogComponent,
    PrintViewComponent
  ],
  /** Modules imported and used within this app module. */
  imports: [
    CommonModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    AppMaterialImporterModule,
    SharedUtilitiesModule,
    HttpClientModule,
    FormsModule,
    MsalModule,
    StoreModule.forRoot({ appState: appStateReducer }),
    EffectsModule.forRoot([]),
    EffectsModule.forFeature([AppEffects]),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: true,
      autoPause: true,
      trace: false,
      traceLimit: 75,
      connectInZone: true
    })
  ],
  /** Application-wide injectable services and configurations. */
  providers: [
    { provide: MAT_TABS_CONFIG, useValue: { animationDuration: '0ms' } },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    // Order matters: AuthTokenInterceptor must attach the token before AuthExpiryInterceptor
    // can retry a 401, since the retry is replayed downstream of it.
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthTokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthExpiryInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerErrorInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiLoggingInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    // Empty except in the `e2e` build, where it replaces MSAL_INSTANCE with a local fake.
    // Must stay last so it wins. See src/e2e/README.md.
    ...authBypassProviders
  ],
  /** Schema definitions to allow custom and unknown elements */
  schemas: [NO_ERRORS_SCHEMA, CUSTOM_ELEMENTS_SCHEMA],
  /** Components bootstrapped at app startup. */
  bootstrap: [AppComponent, MsalRedirectComponent]
})
/**
 * The main application module bootstrapping the root component and setting up
 * application-wide services, including MSAL authentication and NgRx state management.
 */
export class AppModule { }
